import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Button, Flex, Form, Input, InputNumber, Modal, Space, Typography, message } from 'antd'
import { useForm, useWatch } from 'antd/es/form/Form'
import ClientAutoComplete from '../../../../components/form-items/client-auto-complete/client-auto-complete'
import { RIBBON_ORDER_ITEM_UNIT, RIBBON_PRODUCT_TYPE_SLUG } from '../../../../constants/ribbon'
import { requiredInputNumberRules, requiredRules } from '../../../../constants/forms/validation/rules'
import { calcRibbonProfitPreview } from '../../../../helpers/calculations/calc-ribbon-rolls'
import { useCreateOrderMutation } from '../../../../store/api/orders-api'
import {
    useCreateOrderItemMutation,
    useUpdateOrderItemMutation,
} from '../../../../store/api/order-items-api'
import { useLazyGetProductTypesQuery } from '../../../../store/api/product-types-api'
import {
    useCreateRibbonMutation,
    useUpdateAndCalculateRibbonMutation,
} from '../../../../store/api/ribbons-api'
import { useAppSelector } from '../../../../store/store'
import Ribbon from '../../../../types/api/ribbon'
import RibbonSpecs from '../../../../types/api/ribbon-specs'
import { useChooseOpenOrder } from '../../../../helpers/orders/use-choose-open-order'
import { buildRibbonName } from '../../helpers/build-ribbon-name'

interface RibbonFormValues {
    clientDocumentId: string
    rollsCount: number
    costPrice: number
    sellPrice: number
}

function formValuesToSpecs(
    values: RibbonFormValues
): Pick<RibbonSpecs, 'rollsCount' | 'profit' | 'totalProfit' | 'totalCost'> {
    const profit = calcRibbonProfitPreview(values.costPrice, values.sellPrice)
    const totalProfit = Math.round(profit * values.rollsCount * 100) / 100
    const totalCost = Math.round(values.costPrice * values.rollsCount * 100) / 100

    return {
        rollsCount: values.rollsCount,
        profit,
        totalProfit,
        totalCost,
    }
}

function formatRibbonSaveError(error: unknown): string {
    if (error instanceof Error) return error.message

    if (typeof error === 'object' && error !== null) {
        const err = error as {
            data?: { error?: { message?: string }; message?: string }
            error?: string
            message?: string
            status?: number | string
            errorFields?: Array<{ errors?: string[]; name?: unknown }>
        }

        if (err.errorFields?.length) {
            return (
                err.errorFields
                    .flatMap(field => field.errors || [])
                    .filter(Boolean)
                    .join('; ') || 'Проверьте заполнение формы'
            )
        }

        const apiMessage = err.data?.error?.message || err.data?.message || err.message
        if (apiMessage) return apiMessage

        if (err.status) return `Ошибка запроса (${err.status})`

        try {
            return JSON.stringify(error)
        } catch {
            return 'Неизвестная ошибка'
        }
    }

    return String(error)
}

function useSaveRibbon() {
    const user = useAppSelector(state => state.auth.user)
    const chooseOpenOrder = useChooseOpenOrder()

    const [createRibbon] = useCreateRibbonMutation()
    const [updateAndCalculateRibbon] = useUpdateAndCalculateRibbonMutation()
    const [createOrder] = useCreateOrderMutation()
    const [createOrderItem] = useCreateOrderItemMutation()
    const [updateOrderItem] = useUpdateOrderItemMutation()
    const [fetchProductTypes] = useLazyGetProductTypesQuery()

    const resolveRibbonProductTypeId = useCallback(async () => {
        const result = await fetchProductTypes({
            filters: { slug: { $eq: RIBBON_PRODUCT_TYPE_SLUG } },
            pagination: { page: 1, pageSize: 1 },
        }).unwrap()

        const productType = result.data?.[0]
        return productType.documentId
    }, [fetchProductTypes])

    const resolveOrderDocumentId = useCallback(
        async (clientDocumentId: string): Promise<string> => {
            if (!user?.documentId) {
                throw new Error('Пользователь не авторизован')
            }

            const choice = await chooseOpenOrder(clientDocumentId, 'риббон')

            if (choice.kind === 'existing') {
                return choice.documentId
            }

            const orderResult = await createOrder({
                data: {
                    orderStatus: 'draft',
                    paymentTerms: 'prepayment',
                    client: { connect: [clientDocumentId] },
                    manager: { connect: [user.documentId] },
                },
            }).unwrap()

            return orderResult.data.documentId
        },
        [user?.documentId, chooseOpenOrder, createOrder]
    )

    const create = useCallback(
        async (values: RibbonFormValues) => {
            if (!user?.documentId) {
                throw new Error('Пользователь не авторизован')
            }
            if (!values.clientDocumentId) {
                throw new Error('Выберите клиента из списка')
            }

            const specs = formValuesToSpecs(values)
            const name = buildRibbonName()
            const productTypeDocId = await resolveRibbonProductTypeId()
            const orderDocId = await resolveOrderDocumentId(values.clientDocumentId)

            const orderItemResult = await createOrderItem({
                data: {
                    order: { connect: [orderDocId] },
                    product_type: { connect: [productTypeDocId] },
                    name,
                    quantity: specs.rollsCount,
                    unit: RIBBON_ORDER_ITEM_UNIT,
                    itemStatus: 'draft',
                    approvedPrice: values.sellPrice,
                },
            }).unwrap()

            const ribbonResult = await createRibbon({
                data: {
                    order_item: { connect: [orderItemResult.data.documentId] },
                    specs,
                    cost: {
                        costPrice: values.costPrice,
                        sellPrice: values.sellPrice,
                    },
                },
            }).unwrap()

            await updateAndCalculateRibbon({
                docId: ribbonResult.data.documentId,
                body: {
                    data: {
                        specs,
                        cost: {
                            costPrice: values.costPrice,
                            sellPrice: values.sellPrice,
                        },
                    },
                },
            }).unwrap()

            message.success('Риббон создан')
        },
        [
            user?.documentId,
            resolveRibbonProductTypeId,
            resolveOrderDocumentId,
            createOrderItem,
            createRibbon,
            updateAndCalculateRibbon,
        ]
    )

    const update = useCallback(
        async (ribbon: Ribbon, values: RibbonFormValues) => {
            const specs = formValuesToSpecs(values)
            const name = buildRibbonName()

            await updateAndCalculateRibbon({
                docId: ribbon.documentId,
                body: {
                    data: {
                        specs: {
                            ...ribbon.specs,
                            ...specs,
                        },
                        cost: {
                            id: ribbon.cost?.id,
                            costPrice: values.costPrice,
                            sellPrice: values.sellPrice,
                        },
                    },
                },
            }).unwrap()

            if (ribbon.order_item?.documentId) {
                await updateOrderItem({
                    docId: ribbon.order_item.documentId,
                    body: {
                        data: {
                            name,
                            quantity: specs.rollsCount,
                            unit: RIBBON_ORDER_ITEM_UNIT,
                            approvedPrice: values.sellPrice,
                        },
                    },
                }).unwrap()
            }

            message.success('Риббон обновлён')
        },
        [updateAndCalculateRibbon, updateOrderItem]
    )

    return { create, update }
}

interface RibbonFormProps {
    ribbon?: Ribbon | null
    onSuccess: () => void
    onClose: () => void
}

const RibbonForm: FC<RibbonFormProps> = ({ ribbon, onSuccess, onClose }) => {
    const [form] = useForm<RibbonFormValues>()
    const { create, update } = useSaveRibbon()
    const [saving, setSaving] = useState(false)
    const isEdit = Boolean(ribbon)

    const rollsCount = useWatch('rollsCount', form)
    const costPrice = useWatch('costPrice', form)
    const sellPrice = useWatch('sellPrice', form)

    const previewProfit = useMemo(
        () => calcRibbonProfitPreview(costPrice || 0, sellPrice || 0),
        [costPrice, sellPrice]
    )

    const previewTotalProfit = useMemo(
        () => Math.round(previewProfit * (rollsCount || 0) * 100) / 100,
        [previewProfit, rollsCount]
    )

    const previewTotalCost = useMemo(
        () => Math.round((costPrice || 0) * (rollsCount || 0) * 100) / 100,
        [costPrice, rollsCount]
    )

    useEffect(() => {
        if (ribbon) {
            form.setFieldsValue({
                clientDocumentId: ribbon.order_item?.order?.client?.documentId,
                rollsCount: ribbon.specs?.rollsCount,
                costPrice: ribbon.cost?.costPrice,
                sellPrice: ribbon.cost?.sellPrice,
            })
        } else {
            form.resetFields()
            form.setFieldsValue({
                rollsCount: 1,
                costPrice: 0,
                sellPrice: 0,
            })
        }
    }, [ribbon, form])

    const handleSubmit = async () => {
        setSaving(true)
        try {
            const values = await form.validateFields()
            if (ribbon) {
                await update(ribbon, values)
            } else {
                await create(values)
            }
            onSuccess()
            onClose()
        } catch (error) {
            Modal.error({
                title: ribbon ? 'Ошибка обновления риббона' : 'Ошибка создания риббона',
                content: formatRibbonSaveError(error),
            })
        } finally {
            setSaving(false)
        }
    }

    return (
        <Form form={form} layout='vertical'>
            {!isEdit && (
                <>
                    <ClientAutoComplete
                        label='Клиент'
                        onChange={docId => form.setFieldValue('clientDocumentId', docId)}
                    />
                    <Form.Item name='clientDocumentId' hidden rules={requiredRules}>
                        <Input />
                    </Form.Item>
                </>
            )}
            <Form.Item name='rollsCount' label='Кол-во рулонов' rules={requiredInputNumberRules}>
                <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
                name='sellPrice'
                label='Отпускная цена одного рулона'
                rules={requiredInputNumberRules}
            >
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
                name='costPrice'
                label='Себестоимость одного рулона'
                rules={requiredInputNumberRules}
            >
                <InputNumber min={0} step={0.01} style={{ width: '100%' }} />
            </Form.Item>

            <Space direction='vertical' size={0} style={{ marginBottom: 16 }}>
                <Typography.Text type='secondary'>Себестоимость: {previewTotalCost}</Typography.Text>
                <Typography.Text type='secondary'>
                    Прибыль с рулона: {previewProfit}
                </Typography.Text>
                <Typography.Text type='secondary'>Прибыль: {previewTotalProfit}</Typography.Text>
            </Space>

            <Flex justify='end' gap={8}>
                <Button onClick={onClose}>Отмена</Button>
                <Button type='primary' loading={saving} onClick={handleSubmit}>
                    {isEdit ? 'Сохранить' : 'Создать'}
                </Button>
            </Flex>
        </Form>
    )
}

export default RibbonForm
